import { useState } from "react";
import { Users, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime } from "../lib/api";
import { useApi, StatusChip, btn, inputCls } from "./kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export function AudienceEditor({ p, onDone }) {
  const people = useApi("/users");
  const [f, setF] = useState({ ack_required: p.ack_required !== false, audience: p.audience || "everyone", audience_user_ids: p.audience_user_ids || [] });
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setF({ ...f, audience_user_ids: f.audience_user_ids.includes(id) ? f.audience_user_ids.filter((x) => x !== id) : [...f.audience_user_ids, id] });
  const save = async () => {
    setBusy(true);
    try { await api.put(`/policies/${p.id}/audience`, f); toast.success("Saved. Anyone newly included is asked to acknowledge."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const list = (people.data || []).filter((u) => !u.is_demo && u.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="mt-3 space-y-3 rounded-xl border bg-muted/30 p-4" data-testid={`audience-editor-${p.id}`}>
      <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" className="h-5 w-5" checked={f.ack_required} onChange={(e) => setF({ ...f, ack_required: e.target.checked })} data-testid={`audience-required-${p.id}`} /> Staff must acknowledge this policy (due within 30 days)</label>
      {f.ack_required && <>
        <div className="flex flex-wrap gap-4 text-sm">
          {[["everyone", "Everyone"], ["selected", "Only selected people"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2"><input type="radio" name={`aud-${p.id}`} checked={f.audience === k} onChange={() => setF({ ...f, audience: k })} data-testid={`audience-${k}-${p.id}`} /> {l}</label>))}
        </div>
        {f.audience === "selected" && <div>
          <input className={inputCls} placeholder="Search people" aria-label="Search people" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`audience-search-${p.id}`} />
          <p className="mt-2 text-xs text-muted-foreground">{f.audience_user_ids.length} selected</p>
          <div className="mt-1 max-h-56 space-y-1 overflow-y-auto">{list.map((u) => (
            <label key={u.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-white"><input type="checkbox" checked={f.audience_user_ids.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`audience-person-${u.id}`} /> {u.name} <span className="text-xs text-muted-foreground">{u.department}</span></label>))}</div>
        </div>}
      </>}
      <button className={btn.primary} disabled={busy} onClick={save} data-testid={`audience-save-${p.id}`}>Save who acknowledges</button>
    </div>
  );
}

export function AckStatus({ p }) {
  const { data } = useApi(`/policies/${p.id}/acks`);
  const [open, setOpen] = useState(false);
  if (!data || !p.current) return null;
  if (p.ack_required === false) return <p className="mt-3 text-sm text-muted-foreground">Acknowledgement not required.</p>;
  const who = p.audience === "selected" ? `${(p.audience_user_ids || []).length} selected people` : "Everyone";
  return (
    <div className="mt-3 text-sm" data-testid={`ack-status-${p.id}`}>
      <button className="flex items-center gap-2 font-semibold" onClick={() => setOpen(!open)} data-testid={`ack-status-toggle-${p.id}`}>
        <Users className="h-4 w-4" /> {who} · {data.done} of {data.total} acknowledged v{data.ack_version}{data.rows.some((r) => r.overdue) && <StatusChip status="overdue" label={`${data.rows.filter((r) => r.overdue).length} overdue`} />}
      </button>
      {open && <ul className="mt-2 divide-y rounded-lg border bg-white">{data.rows.map((r) => (
        <li key={r.user_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
          <span>{r.name} <span className="text-xs text-muted-foreground">{r.department}</span></span>
          {r.acknowledged_at ? <span className="text-xs text-muted-foreground">v{r.version} · {fmtDateTime(r.acknowledged_at)}{r.via_course && " · in a course"}</span>
            : <StatusChip status={r.overdue ? "overdue" : "not_started"} label={r.overdue ? `Overdue (due ${fmtDate(r.due_date)})` : `Due ${fmtDate(r.due_date)}`} />}
        </li>))}</ul>}
    </div>
  );
}

export function PublishDialog({ p, v, onClose, onDone }) {
  const first = !p.current_version_id;
  const [reack, setReack] = useState(true);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/policies/versions/${v.id}/publish`, { requires_reack: reack });
      toast.success(r.data.requires_reack && p.ack_required !== false ? "Published. Staff have been asked to acknowledge it." : "Published.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="policy-publish-dialog">
        <DialogHeader><DialogTitle>Publish {p.title} v{v.version}?</DialogTitle>
          <DialogDescription>Staff will see this version in Policies straight away.</DialogDescription></DialogHeader>
        {p.ack_required === false ? <p className="text-sm text-muted-foreground">This policy doesn't require acknowledgement.</p>
          : first ? <p className="text-sm">Everyone it applies to will get an email and a notification asking them to acknowledge it within 30 days.</p>
          : <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-0.5 h-5 w-5" checked={reack} onChange={(e) => setReack(e.target.checked)} data-testid="policy-publish-reack" />
              <span><b>Everyone must acknowledge this version again.</b> Untick for small fixes like typos; earlier acknowledgements will still count.</span></label>}
        <button className={`${btn.primary} w-full`} disabled={busy} onClick={go} data-testid="policy-publish-confirm"><Send className="h-4 w-4" /> Publish</button>
      </DialogContent>
    </Dialog>
  );
}
